import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Code, Copy, ExternalLink, Plus, Trash2, DollarSign, Mail, ChevronDown, ChevronUp, CheckCircle, X, Info, ToggleLeft, ToggleRight, FileText, Send, Loader2 } from "lucide-react";
import api from "../../lib/api";


const EmbedEnginePage = () => {
  const { token } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [courses, setCourses] = useState([]);
  const [providers, setProviders] = useState([]);
  const [expandedToken, setExpandedToken] = useState(null);
  const [instructions, setInstructions] = useState({});
  const [emailModal, setEmailModal] = useState(null);
  const [emailForm, setEmailForm] = useState({ email: "", name: "" });
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    course_id: "", embed_type: "iframe", lms_provider: "",
    client_org_name: "", expires_in_days: 30, is_billable: false,
    billing_amount: null, billing_frequency: "monthly",
  });

  const fetchData = useCallback(async () => {
    try {
      const [tokensRes, statsRes, coursesRes, providersRes] = await Promise.all([
        api.get("/embed/tokens"),
        api.get("/embed/stats"),
        api.get("/courses?limit=200"),
        api.get("/embed/lms-providers"),
      ]);
      setTokens(tokensRes.data.tokens || []);
      setStats(statsRes.data);
      setCourses(Array.isArray(coursesRes.data) ? coursesRes.data : coursesRes.data.courses || []);
      setProviders(providersRes.data.providers || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!form.course_id) return toast.error("Select a course");
    if (!form.lms_provider) return toast.error("Select an LMS provider");
    try {
      const res = await api.post("/embed/generate", form);
      toast.success(`Embed token generated for ${res.data.course_title}`);
      setShowCreate(false);
      setForm({ course_id: "", embed_type: "iframe", lms_provider: "", client_org_name: "", expires_in_days: 30, is_billable: false, billing_amount: null, billing_frequency: "monthly" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate"); }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this embed token?")) return;
    try {
      await api.post("/embed/tokens/${id}/revoke", {});
      toast.success("Token revoked");
      fetchData();
    } catch (e) { toast.error("Failed to revoke"); }
  };

  const toggleBilling = async (id, currentBillable) => {
    try {
      await api.patch("/embed/tokens/${id}/billing", { is_billable: !currentBillable });
      toast.success(`Billing ${!currentBillable ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (e) { toast.error("Failed to update billing"); }
  };

  const loadInstructions = async (tokenId) => {
    if (expandedToken === tokenId) {
      setExpandedToken(null);
      return;
    }
    try {
      const res = await api.get("/embed/tokens/${tokenId}/instructions");
      setInstructions(prev => ({ ...prev, [tokenId]: res.data }));
      setExpandedToken(tokenId);
    } catch (e) { toast.error("Failed to load instructions"); }
  };

  const sendEmail = async (tokenId) => {
    if (!emailForm.email) return toast.error("Email is required");
    setSending(true);
    try {
      await api.post("/embed/tokens/${tokenId}/email-instructions", emailForm);
      toast.success(`Instructions sent to ${emailForm.email}`);
      setEmailModal(null);
      setEmailForm({ email: "", name: "" });
    } catch (e) { toast.error("Failed to send email"); }
    setSending(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) return (
    <AdminLayout title="Embed Engine">
      <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
    </AdminLayout>
  );

  const providerName = (key) => providers.find(p => p.id === key)?.name || key || "Generic";

  return (
    <AdminLayout title="Embed Engine">
    <div className="space-y-6" data-testid="embed-engine-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Embed Engine</h1>
          <p className="text-sm text-muted-foreground mt-1">Embed Odesie courses into external LMS platforms with guided setup</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-embed-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Embed
        </Button>
      </div>

      {/* Stats Strip */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: "Total Embeds", value: stats.total_tokens || 0, color: "#1B4F8A" },
          { label: "Active", value: stats.active_tokens || 0, color: "#0E8A55" },
          { label: "Revoked", value: stats.revoked_tokens || 0, color: "#C7322A" },
          { label: "Billable", value: stats.billable_tokens || 0, color: "#7C3AED" },
          { label: "Total Launches", value: stats.total_launches || 0, color: "#F5841F" },
        ].map((s, i) => (
          <div key={i} className="bg-background border rounded-xl p-4" style={{ borderTop: `3px solid ${s.color}` }}>
            <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">{s.label}</p>
            <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCreate(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-embed-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Create Embed Token</h2>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              {/* Course Selection */}
              <div>
                <label className="text-sm font-medium">Course *</label>
                <select className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-background" value={form.course_id} onChange={e => setForm({...form, course_id: e.target.value})} data-testid="embed-course-select">
                  <option value="">Select a course...</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>

              {/* LMS Provider Selection */}
              <div>
                <label className="text-sm font-medium">Target LMS Platform *</label>
                <p className="text-xs text-muted-foreground mb-2">Select the LMS where this course will be embedded</p>
                <div className="grid grid-cols-2 gap-2">
                  {providers.map(p => (
                    <button key={p.id} onClick={() => {
                      setForm({...form, lms_provider: p.id, embed_type: p.embed_method.includes("LTI") ? "lti" : "iframe"});
                    }}
                      className={`p-3 border rounded-lg text-left transition-all ${form.lms_provider === p.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'hover:border-muted-foreground/50'}`}
                      data-testid={`lms-provider-${p.id}`}
                    >
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-[10.5px] text-muted-foreground">{p.embed_method}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Embed Type */}
              <div>
                <label className="text-sm font-medium">Embed Method</label>
                <select className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-background" value={form.embed_type} onChange={e => setForm({...form, embed_type: e.target.value})} data-testid="embed-type-select">
                  <option value="iframe">iFrame</option>
                  <option value="lti">LTI 1.3</option>
                </select>
              </div>

              {/* Client Org */}
              <div>
                <label className="text-sm font-medium">Client Organization</label>
                <Input value={form.client_org_name} onChange={e => setForm({...form, client_org_name: e.target.value})} placeholder="e.g., Partner Corp" className="mt-1" data-testid="embed-client-org" />
              </div>

              {/* Expiry */}
              <div>
                <label className="text-sm font-medium">Expires In (Days)</label>
                <Input type="number" value={form.expires_in_days} onChange={e => setForm({...form, expires_in_days: parseInt(e.target.value) || 30})} className="mt-1" />
              </div>

              {/* Billing */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <label className="text-sm font-medium">Billable Embed</label>
                  </div>
                  <button onClick={() => setForm({...form, is_billable: !form.is_billable})}
                    className={`w-10 h-5 rounded-full transition-colors relative ${form.is_billable ? 'bg-green-500' : 'bg-muted-foreground/30'}`} data-testid="billing-toggle">
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_billable ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                {form.is_billable && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Amount ($)</label>
                      <Input type="number" value={form.billing_amount || ""} onChange={e => setForm({...form, billing_amount: parseFloat(e.target.value) || null})} placeholder="0.00" className="mt-1 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Frequency</label>
                      <select className="w-full mt-1 h-8 rounded-md border px-2 text-sm bg-background" value={form.billing_frequency} onChange={e => setForm({...form, billing_frequency: e.target.value})}>
                        <option value="one_time">One-time</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleGenerate} data-testid="confirm-generate-btn">Generate Embed</Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Instructions Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setEmailModal(null)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="email-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Email Integration Instructions</h2>
            <p className="text-sm text-muted-foreground">Send the LMS integration guide to a client administrator.</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Recipient Email *</label>
                <Input type="email" value={emailForm.email} onChange={e => setEmailForm({...emailForm, email: e.target.value})} placeholder="admin@client.com" className="mt-1" data-testid="email-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Recipient Name</label>
                <Input value={emailForm.name} onChange={e => setEmailForm({...emailForm, name: e.target.value})} placeholder="John Doe" className="mt-1" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEmailModal(null)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => sendEmail(emailModal)} disabled={sending} data-testid="send-email-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {sending ? "Sending..." : "Send Instructions"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tokens List */}
      <div className="space-y-3">
        {tokens.length === 0 ? (
          <div className="border rounded-xl p-12 text-center bg-background">
            <Code className="w-12 h-12 mx-auto mb-3 opacity-30 text-muted-foreground" />
            <p className="font-bold text-lg" style={{ fontFamily: "'Sora', sans-serif" }}>No embed tokens yet</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Generate your first embed to start delivering courses in external LMS platforms</p>
            <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Create Your First Embed
            </Button>
          </div>
        ) : tokens.map(t => (
          <div key={t.id} className="border rounded-xl bg-background shadow-sm overflow-hidden" data-testid={`embed-token-${t.id}`}>
            {/* Token Header */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  <ExternalLink className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm">{t.course_title}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-blue-100 text-blue-800">{t.embed_type}</span>
                    {t.lms_provider && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-purple-100 text-purple-800">{providerName(t.lms_provider)}</span>}
                    {t.is_billable && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-800 flex items-center gap-0.5"><DollarSign className="w-3 h-3" />Billable</span>}
                    {t.is_active ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-green-100 text-green-800">Active</span>
                      : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-800">Revoked</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">Client: {t.client_org_name} | Launches: {t.launch_count} | Token: <code className="bg-muted px-1 rounded">{t.token_hash?.substring(0, 12)}...</code></p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => copyToClipboard(t.token_hash)} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Copy token"><Copy className="w-4 h-4 text-muted-foreground" /></button>
                <button onClick={() => loadInstructions(t.id)} className="p-2 rounded-lg hover:bg-muted transition-colors" title="View instructions" data-testid={`instructions-btn-${t.id}`}>
                  {expandedToken === t.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
                <button onClick={() => setEmailModal(t.id)} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Email instructions" data-testid={`email-btn-${t.id}`}>
                  <Mail className="w-4 h-4 text-muted-foreground" />
                </button>
                <button onClick={() => toggleBilling(t.id, t.is_billable)} className="p-2 rounded-lg hover:bg-muted transition-colors" title={t.is_billable ? "Disable billing" : "Enable billing"} data-testid={`billing-btn-${t.id}`}>
                  {t.is_billable ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                </button>
                {t.is_active && (
                  <button onClick={() => handleRevoke(t.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors" title="Revoke token" data-testid={`revoke-token-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Expanded Instructions */}
            {expandedToken === t.id && instructions[t.id] && (
              <div className="border-t bg-muted/30 p-4" data-testid={`instructions-panel-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Integration Guide: {instructions[t.id].provider}
                    <span className="text-[10px] font-medium text-muted-foreground uppercase">({instructions[t.id].embed_method})</span>
                  </h4>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEmailModal(t.id)} data-testid={`email-from-instructions-${t.id}`}>
                    <Mail className="w-3 h-3 mr-1" /> Email to Client
                  </Button>
                </div>
                <ol className="space-y-2">
                  {instructions[t.id].instructions.map((step, idx) => (
                    <li key={idx} className="flex gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">{idx + 1}</span>
                      <span className="pt-0.5 text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
                {instructions[t.id].embed_url && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between bg-background border rounded-lg px-3 py-2">
                      <div>
                        <span className="text-[10px] uppercase text-muted-foreground font-bold">Embed URL</span>
                        <p className="text-xs font-mono">{instructions[t.id].embed_url}</p>
                      </div>
                      <button onClick={() => copyToClipboard(instructions[t.id].embed_url)} className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                    {instructions[t.id].iframe_code && (
                      <div className="flex items-center justify-between bg-background border rounded-lg px-3 py-2">
                        <div>
                          <span className="text-[10px] uppercase text-muted-foreground font-bold">iFrame Code</span>
                          <p className="text-xs font-mono truncate max-w-[500px]">{instructions[t.id].iframe_code}</p>
                        </div>
                        <button onClick={() => copyToClipboard(instructions[t.id].iframe_code)} className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                )}
                {t.is_billable && (
                  <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-xs text-amber-800">
                    <DollarSign className="w-4 h-4 flex-shrink-0" />
                    <span>This embed is billable: <strong>${t.billing_amount || "0.00"}</strong> / {t.billing_frequency || "monthly"}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    </AdminLayout>
  );
};

export default EmbedEnginePage;
